import { create } from 'zustand';
import useLoadingStore from './use-loading';
import { makeRequest } from 'api/api-client';
import { HTTP } from 'constants/http-method';
import { API_ENDPOINTS } from 'api/endpoints';

const useUserManagementStore = create((set) => ({
  usersLoading: false,
  totalUsers: 0,
  users: [],
  pagination: {
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
  },

  // Function to reset currentPage only when an action happens
  resetCurrentPage: () => {
    set((state) => ({
      pagination: {
        ...state.pagination,
        currentPage: 1,
      },
    }));
  },

  // Function to get user
  getUser: async ({ query = null, successCallback }) => {
    set({ usersLoading: true });
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_USER,
        null,
        query
      );

      if (response.success) {
        set({
          totalUsers: response.data?.pagination?.totalOrganizationUsers || 0,
          users: response.data?.data || [],
          pagination: {
            pageSize: response.data?.pagination?.limit,
            totalItems: response.data?.pagination?.total,
            currentPage: response.data?.pagination?.page,
            totalPages: response.data?.pagination?.totalpages,
          },
        });
        successCallback && successCallback();
      }
    } catch (error) {
    } finally {
      set({ usersLoading: false });
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to create the user
  createUser: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.CREATE_USER,
        data
      );

      if (response.success) {
        await useUserManagementStore.getState().getUser({
          successCallback: () => {
            useUserManagementStore.getState().resetCurrentPage(); // Reset page
            successCallback && successCallback(response);
          },
        });
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to delete user
  deleteUser: async ({ id, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.DELETE,
        API_ENDPOINTS.DELETE_USER,
        null,
        null,
        id
      );

      if (response.success) {
        set((state) => {
          const updatedUsers = state.users.filter((user) => user.id !== id);
          const updatedTotalItems = updatedUsers.length;
          const updatedTotalPages = Math.ceil(
            updatedTotalItems / state.pagination.pageSize
          );

          return {
            users: updatedUsers,
            pagination: {
              ...state.pagination,
              totalItems: updatedTotalItems,
              totalPages: updatedTotalPages,
            },
          };
        });
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to update user
  updateUser: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.UPDATE_USER,
        data,
        null,
        id
      );

      if (response.success) {
        set((state) => {
          // Find the updated user
          const updatedUser = state.users.find((user) => user.id === id);

          // If the user is found, update it and move it to the first position
          if (updatedUser) {
            const updatedUsers = [
              { ...updatedUser, ...response?.data?.data }, // Updated user
              ...state.users.filter((user) => user.id !== id), // Rest of the users
            ];

            return {
              users: updatedUsers,
              pagination: {
                ...state.pagination,
                currentPage: 1,
              },
            };
          }

          // If the user is not found, return the state as is
          return state;
        });

        successCallback && successCallback(response);
      }
    } catch (error) {
      // Handle error
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to active/inactive user
  toggleUserStatus: async ({ id, data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.PUT,
        API_ENDPOINTS.ACTIVE_INACTIVE_USER,
        data,
        null,
        id
      );
      if (response.success) {
        set((state) => ({
          users: state.users.map((user) =>
            user.id === id ? { ...user, ...data } : user
          ),
        }));
        successCallback && successCallback(response);
      }
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },

  // Function to set user password
  setUserPassword: async ({ data, successCallback }) => {
    useLoadingStore.getState().setLoading(true);
    try {
      const { data: response } = await makeRequest(
        HTTP.POST,
        API_ENDPOINTS.USER_SET_PASSWORD,
        data
      );
      response.success && successCallback(response);
    } catch (error) {
    } finally {
      useLoadingStore.getState().setLoading(false);
    }
  },
}));

export default useUserManagementStore;
